import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http, API } from "../../lib/api";
import { Plus, Pencil, Trash2, ArrowLeft, Loader2, ExternalLink, Clock, Users } from "lucide-react";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await http.get("/admin/posts");
      setPosts(res.data);
    } catch (e) { toast.error("Failed to load posts"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await http.delete(`/admin/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success("Post deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-[#EFEBDE] pt-28 pb-24">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-[#16281C]/12 pb-8">
          <div>
            <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#66766A] hover:text-[#2C5F3A] transition-colors">
              <ArrowLeft size={14} /> View blog
            </Link>
            <h1 className="mt-5 font-display font-semibold text-4xl md:text-5xl tracking-tighter text-[#16281C]">Manage Posts</h1>
          </div>
          <div className="flex flex-wrap gap-3 self-start">
            <a
              href={`${API}/admin/subscribers.csv`}
              data-testid="export-subscribers"
              className="inline-flex items-center gap-2 border border-[#16281C]/20 text-[#16281C] font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-4 hover:border-[#2C5F3A] hover:text-[#2C5F3A] transition-colors"
            >
              <Users size={15} /> Export subscribers
            </a>
            <button
              onClick={() => navigate("/admin/blog/new")}
              data-testid="new-post-button"
              className="inline-flex items-center gap-2 bg-[#2C5F3A] text-[#EFEBDE] font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-4 hover:bg-[#367A49] transition-colors"
            >
              <Plus size={15} /> New Post
            </button>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#2C5F3A]" /></div>
          ) : posts.length === 0 ? (
            <p className="text-center py-20 font-body text-[#4C5C50]">No posts yet. Write your first one.</p>
          ) : (
            <div className="grid gap-3">
              {posts.map((p) => (
                <div key={p.id} data-testid="admin-post-row" className="flex items-center justify-between gap-4 bg-[#FAF8F1] border border-[#16281C]/12 p-5 md:p-6">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className={`font-mono text-[9px] tracking-[0.15em] uppercase px-2 py-1 ${p.status === "published" ? "bg-[#2C5F3A] text-[#EFEBDE]" : "bg-[#E7E2D2] text-[#66766A] border border-[#16281C]/15"}`}>
                        {p.status}
                      </span>
                      <h3 className="font-display font-semibold text-lg md:text-xl tracking-tight text-[#16281C] truncate">{p.title}</h3>
                    </div>
                    <div className="mt-2 flex items-center gap-4 font-mono text-[10px] tracking-[0.15em] uppercase text-[#8B978C]">
                      <span>{fmtDate(p.updated_at)}</span>
                      <span className="inline-flex items-center gap-1"><Clock size={11} /> {p.read_time} min</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {p.status === "published" && (
                      <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-[#66766A] hover:text-[#2C5F3A]" title="View"><ExternalLink size={16} /></a>
                    )}
                    <Link to={`/admin/blog/${p.id}/edit`} data-testid="edit-post" className="p-2 text-[#66766A] hover:text-[#2C5F3A]" title="Edit"><Pencil size={16} /></Link>
                    <button onClick={() => del(p.id)} data-testid="delete-post" className="p-2 text-[#66766A] hover:text-red-600" title="Delete"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
